import { useState } from 'react';
import useFetch from '../../../hooks/useFetch';
import { buildEndpoint } from '../EndPoints';
import { ServiceEvents } from '../shared/interfaces';

export interface ServicePricingPlan {
    id: number;
    service_type: string;
    service_type_display: string;
    plan_name: string;
    base_price: number;
    base_price_formatted: string;
    currency: string;
    description: string;
    features: string[];
    exclusions: string[];
    is_default: boolean;
    display_order: number;
    is_active: boolean;
}

const useServicePricingService = () => {
    const { doGet, doPost } = useFetch();
    
    // Estados de carga
    const [fetchingPlans, setFetchingPlans] = useState(false);
    const [fetchingGetServicePricings, setFetchingGetServicePricings] = useState(false);
    const [fetchingGetInapiPrice, setFetchingGetInapiPrice] = useState(false);
    const [fetchingEditServicePricing, setFetchingEditServicePricing] = useState(false);
    const [fetchingUpdateServicePricing, setFetchingUpdateServicePricing] = useState(false);
    const [fetchingStoreServicePricing, setFetchingStoreServicePricing] = useState(false);
    const [fetchingDeleteServicePricing, setFetchingDeleteServicePricing] = useState(false);
    const [fetchingActiveServicePricing, setFetchingActiveServicePricing] = useState(false);

    const BASE_PATH = 'brand-registrations/service-pricing';

    /**
     * Obtiene todos los precios de servicio (mantenedor)
     */
    const getServicePricings = (filters: any = {}, events: ServiceEvents = {}) => {
        const queryString = Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== '')
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
            
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}${queryString ? `?${queryString}` : ''}`),
            setFetching: setFetchingGetServicePricings
        });
    };

    /**
     * Obtiene el precio por defecto de INAPI
     */
    const getInapiPrice = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/inapi-price`),
            setFetching: setFetchingGetInapiPrice
        });
    };

    /**
     * Obtiene los planes disponibles por tipo de servicio
     */
    const getPlansByServiceType = (serviceType: string, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/plans/${serviceType}`),
            setFetching: setFetchingPlans
        });
    };

    /**
     * Obtiene los planes para verificacion de registro de marca
     */
    const getVerificationPlans = (events: ServiceEvents = {}) => {
        getPlansByServiceType('brand_verification', events);
    };

    /**
     * Obtiene los planes para presentacion INAPI
     */
    const getInapiPlans = (events: ServiceEvents = {}) => {
        getPlansByServiceType('brand_inapi', events);
    };

    /**
     * Obtiene un precio de servicio para editar
     */
    const editServicePricing = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/edit`),
            setFetching: setFetchingEditServicePricing
        });
    };

    /**
     * Crea un nuevo precio de servicio
     */
    const storeServicePricing = (data: any, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/store`),
            body: data,
            requestType: 'json',
            setFetching: setFetchingStoreServicePricing
        });
    };

    /**
     * Actualiza un precio de servicio
     */
    const updateServicePricing = (id: number, data: any, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/update`),
            body: data,
            requestType: 'json',
            setFetching: setFetchingUpdateServicePricing
        });
    };

    /**
     * Elimina un precio de servicio
     */
    const deleteServicePricing = (id: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/delete`),
            setFetching: setFetchingDeleteServicePricing
        });
    };

    /**
     * Activa o desactiva un precio de servicio
     */
    const activeServicePricing = (id: number, active: boolean, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/active`),
            body: { is_active: active },
            setFetching: setFetchingActiveServicePricing
        });
    };

    return {
        // Estados
        fetchingPlans,
        fetchingGetServicePricings,
        fetchingGetInapiPrice,
        fetchingEditServicePricing,
        fetchingUpdateServicePricing,
        fetchingStoreServicePricing,
        fetchingDeleteServicePricing,
        fetchingActiveServicePricing,
        // Metodos
        getServicePricings,
        getInapiPrice,
        getPlansByServiceType,
        getVerificationPlans,
        getInapiPlans,
        editServicePricing,
        storeServicePricing,
        updateServicePricing,
        deleteServicePricing,
        activeServicePricing
    };
};

export default useServicePricingService;
